import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { sendRegisterOTP, register, googleAuth } from '../services/api';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { GoogleLogin } from '@react-oauth/google';
import '../styles/global.css';
import '../styles/Auth.css';

const Register = () => {
  const [step, setStep]     = useState<'form' | 'otp'>('form');
  const [form, setForm]     = useState({ name: '', email: '', password: '' });
  const [otp, setOtp]       = useState('');
  const [loading, setLoading] = useState(false);
  const { loginUser }       = useAuth();
  const navigate            = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Step 1 — Send OTP
  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!executeRecaptcha) return toast.error('Captcha not ready!');
    setLoading(true);
    try {
      const captchaToken = await executeRecaptcha('register');
      await sendRegisterOTP({ email: form.email, captchaToken });
      toast.success('OTP sent to your email!');
      setStep('otp');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error sending OTP');
    } finally { setLoading(false); }
  };

  // Step 2 — Verify OTP & Register
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Auto-generate username from email
      const autoUsername = form.email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      const res = await register({ ...form, username: autoUsername, otp });
      loginUser(res.data);
      toast.success('Account created! 🎉');
      navigate('/dashboard');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally { setLoading(false); }
  };

  // Google Login
  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      const res = await googleAuth({ credential: credentialResponse.credential });
      loginUser(res.data);
      toast.success('Welcome! 🎉');
      navigate('/dashboard');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Google login failed');
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-orb1" />
      <div className="auth-orb2" />

      <div className="auth-wrap">
        <div className="auth-logo">
          <Link to="/" style={{ textDecoration: 'none' }}>
            <div className="auth-logo-text">my<em>linx</em></div>
          </Link>
          <p className="auth-logo-sub">
            {step === 'form' ? 'Create your free page in 60 seconds' : 'Verify your email'}
          </p>
        </div>

        <div className="auth-card">

          {/* ── STEP 1: FORM ── */}
          {step === 'form' && (
            <>
              {/* Google OAuth — PRIMARY */}
              <div className="google-primary-wrap">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => toast.error('Google login failed')}
                  theme="outline"
                  shape="rectangular"
                  width="100%"
                  text="signup_with"
                />
              </div>

              <div className="auth-divider"><span>or sign up with email</span></div>

              {/* Email Form — SECONDARY */}
              <form onSubmit={handleSendOTP}>
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input type="text" name="name"
                    value={form.name} onChange={handleChange}
                    placeholder="John Doe" required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input type="email" name="email"
                    value={form.email} onChange={handleChange}
                    placeholder="john@gmail.com" required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input type="password" name="password"
                    value={form.password} onChange={handleChange}
                    placeholder="••••••••" required
                    className="form-input"
                  />
                </div>

                <button type="submit" disabled={loading} className="auth-btn">
                  {loading ? 'Sending OTP...' : 'Create Account →'}
                </button>
              </form>

              {/* Trust line */}
              <div className="auth-trust-row">
                <span>✓ Free forever</span>
                <span>✓ No credit card</span>
                <span>✓ Setup in 60s</span>
              </div>
            </>
          )}

          {/* ── STEP 2: OTP ── */}
          {step === 'otp' && (
            <form onSubmit={handleRegister}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📧</div>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.7 }}>
                  OTP sent to<br />
                  <strong style={{ color: 'var(--ink)' }}>{form.email}</strong>
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Enter OTP</label>
                <input type="text" value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="••••••"
                  maxLength={6} required
                  className="form-input otp-input"
                  autoFocus
                />
              </div>

              <button type="submit" disabled={loading} className="auth-btn">
                {loading ? 'Verifying...' : 'Verify & Create Account →'}
              </button>

              <button type="button" onClick={() => setStep('form')}
                style={{ width: '100%', marginTop: 12, background: 'none', border: 'none', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }}
              >
                ← Change email
              </button>
            </form>
          )}

          <div className="form-divider" />

          <p className="auth-footer-text">
            Already have an account?{' '}
            <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;